import { z } from 'zod';
import { spawn } from '../../utils/translations';
import { prependHttpsIfNeeded } from '../GithubManifestInstallationDialog/utils';
import {
  CONFIGURE_APP_FIELDS,
  CONFIGURE_MT_APP_FIELDS,
  CONFIGURE_TOKEN_FIELDS,
} from './consts';
import {
  findGitlabGroupUrlOverlaps,
  getFirstSegmentOfUrl,
  splitGitlabUrlLines,
} from './utils';

const spawnAppValidations = spawn(
  'dialogs.gitlab-installation.configure-app.form-validations',
);

const spawnTokenValidations = spawn(
  'dialogs.gitlab-installation.configure-token.form-validations',
);

const spawnGatTokenValidations = spawn(
  'dialogs.gitlab-installation.configure-token-gat.form-validations',
);

export const getConfigureTokenFormSchema = () => {
  return z.object({
    [CONFIGURE_TOKEN_FIELDS.ACCESS_TOKEN]: z
      .string()
      .min(1, spawnTokenValidations('access-token.required')),
    // One group URL per line — token-first (Option A) provisions every listed
    // group from the single pasted service-account token.
    [CONFIGURE_TOKEN_FIELDS.GITLAB_URLS]: getGitlabUrlsField(
      spawnTokenValidations,
    ),
  });
};

export const getConfigureGatTokenFormSchema = () => {
  return z.object({
    [CONFIGURE_TOKEN_FIELDS.ACCESS_TOKEN]: z
      .string()
      .trim()
      .min(1, spawnGatTokenValidations('access-token.required')),
    // A Group Access Token is scoped to a single top-level group, so the GAT
    // flow takes exactly one group URL.
    [CONFIGURE_TOKEN_FIELDS.GITLAB_URL]: getGitlabUrlField(
      spawnGatTokenValidations,
    ),
  });
};

const spawnMTAppValidations = spawn(
  'dialogs.gitlab-installation.configure-app-mt.form-validations',
);

export const getConfigureAppFormSchema = () => {
  return z.object({
    [CONFIGURE_APP_FIELDS.CLIENT_ID]: z
      .string()
      .min(1, spawnAppValidations('client-id.required')),
    [CONFIGURE_APP_FIELDS.CLIENT_SECRET]: z
      .string()
      .min(1, spawnAppValidations('client-secret.required')),
    // Accepts one group URL per line — a single line is the classic
    // single-group flow; multiple lines route through configure-multi (B-v1a).
    [CONFIGURE_APP_FIELDS.GITLAB_URL]: getGitlabUrlsField(spawnAppValidations),
  });
};

const getGitlabUrlsField = (spawnValidations: ReturnType<typeof spawn>) => {
  return z
    .string()
    .min(1, spawnValidations('gitlab-url.required'))
    .check((ctx) => {
      const lines = splitGitlabUrlLines(ctx.value);

      if (lines.length === 0) {
        ctx.issues.push({
          code: 'custom',
          message: spawnValidations('gitlab-url.required'),
          input: ctx.value,
          path: [],
        });

        return;
      }

      lines.forEach((line) => {
        let url: URL;

        try {
          url = new URL(prependHttpsIfNeeded(line));

          if (!/\.\w{2,}$/.test(url.hostname)) throw new Error();
        } catch {
          ctx.issues.push({
            code: 'custom',
            message: spawnValidations('gitlab-url.invalid-url'),
            input: line,
            path: [],
          });

          return;
        }

        const firstSegment = getFirstSegmentOfUrl(url);

        if (firstSegment === 'groups') {
          ctx.issues.push({
            code: 'custom',
            message: spawnValidations('gitlab-url.group-page-not-group-url'),
            input: line,
            path: [],
          });
        } else if (!firstSegment) {
          ctx.issues.push({
            code: 'custom',
            message: spawnValidations('gitlab-url.missing-group-name'),
            input: line,
            path: [],
          });
        }
      });

      // Overlap detection runs only after per-line validation passes —
      // compounding it onto a malformed-line error is noisier than fixing
      // the per-line issue first.
      if (ctx.issues.length === 0) {
        findGitlabGroupUrlOverlaps(lines).forEach(({ parent, child }) => {
          ctx.issues.push({
            code: 'custom',
            message: spawnValidations('gitlab-url.parent-subgroup-overlap', {
              parent,
              child,
            }),
            input: child,
            path: [],
          });
        });
      }
    });
};

export const getConfigureMTAppFormSchema = () => {
  return z.object({
    [CONFIGURE_MT_APP_FIELDS.GITLAB_URL]: getGitlabUrlField(
      spawnMTAppValidations,
    ),
  });
};

/**
 * Single group-URL field: trims, then validates a well-formed GitLab group URL.
 * @param spawnValidations i18n validation-message factory for this flow.
 * @returns A Zod string schema for a single GitLab group URL.
 */
const getGitlabUrlField = (spawnValidations: ReturnType<typeof spawn>) => {
  return z
    .string()
    .trim()
    .min(1, spawnValidations('gitlab-url.required'))
    .check((ctx) => {
      try {
        const urlToValidate = prependHttpsIfNeeded(ctx.value);
        const url = new URL(urlToValidate);

        if (!/\.\w{2,}$/.test(url.hostname)) {
          throw new Error();
        }
      } catch {
        ctx.issues.push({
          code: 'custom',
          message: spawnValidations('gitlab-url.invalid-url'),
          input: ctx.value,
          path: [],
        });
      }
    })
    .check((ctx) => {
      try {
        const url = new URL(prependHttpsIfNeeded(ctx.value));
        const firstSegment = getFirstSegmentOfUrl(url);

        if (firstSegment === 'groups') {
          ctx.issues.push({
            code: 'custom',
            message: spawnValidations('gitlab-url.group-page-not-group-url'),
            input: ctx.value,
            path: [],
          });
        }
      } catch {
        // URL parsing already handled in previous check
      }
    })
    .check((ctx) => {
      try {
        const urlToValidate = prependHttpsIfNeeded(ctx.value);
        const url = new URL(urlToValidate);
        const groupName = getFirstSegmentOfUrl(url);

        if (!groupName) {
          ctx.issues.push({
            code: 'custom',
            message: spawnValidations('gitlab-url.missing-group-name'),
            input: ctx.value,
            path: [],
          });
        }
      } catch {
        // URL parsing already handled in previous check
      }
    });
};
